import { Box, Button, Card, CardHeader, Container, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
import { Budget } from "../../components/dashboard/budget";
import { TasksProgress } from "../../components/dashboard/tasks-progress";
import { TotalCustomers } from "../../components/dashboard/total-customers";
import { TotalProfit } from "../../components/dashboard/total-profit";
import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import { forwardRef, useEffect, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import NewSalesChart from "./NewSalesChart";

import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PriceCheckRoundedIcon from "@mui/icons-material/PriceCheckRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import ExtensionRoundedIcon from "@mui/icons-material/ExtensionRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import SportsEsportsRoundedIcon from "@mui/icons-material/SportsEsportsRounded";
import MilitaryTechRoundedIcon from "@mui/icons-material/MilitaryTechRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CasinoRoundedIcon from "@mui/icons-material/CasinoRounded";

const HappyRangeDateInput = forwardRef(({ value, onClick, startDate, endDate }, ref) => {
    let displayText = "Tất cả từ trước đến giờ...";
    if (startDate && endDate) {
        const sFmt = new Date(startDate).toISOString().slice(0, 10);
        const eFmt = new Date(endDate).toISOString().slice(0, 10);
        displayText = sFmt === eFmt ? sFmt : `${sFmt} ~ ${eFmt}`;
    } else if (startDate) {
        displayText = new Date(startDate).toISOString().slice(0, 10);
    }

    return (
        <button type="button" className="happy-single-date-input" onClick={onClick} ref={ref} style={{ width: "220px", height: "34px", padding: "0 10px", margin: 0 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayText}</span>
            <svg className="happy-range-calendar" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M7 3v3m10-3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v14H4V6a1 1 0 0 1 1-1Z" /></svg>
        </button>
    );
});

axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

function Dashboard() {
    const [data, setData] = useState(() => {
        try {
            const saved = localStorage.getItem("happy_admin_dashboard_cache");
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            return null;
        }
    });
    const [isLoading, setLoading] = useState(() => !data);
    const [isGuideOpen, setIsGuideOpen] = useState(false);

    // Happy dashboard opens with all time selected by default.
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    const fetchStatistic = async (start, end) => {
        if (!data) {
            setLoading(true);
        }
        try {
            let s = null;
            let e = null;

            if (start) {
                s = new Date(start);
                s.setHours(0, 0, 0, 0);
            }
            if (end) {
                e = new Date(end);
                e.setHours(23, 59, 59, 999);
            }

            const params = {
                dateStart: s ? s.toISOString() : null,
                dateEnd: e ? e.toISOString() : null
            };

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/statistic/getalladmin`, { params });

            if (res.data.success) {
                setData(res.data.data);
                if (!start && !end) {
                    try {
                        localStorage.setItem("happy_admin_dashboard_cache", JSON.stringify(res.data.data));
                    } catch (e) {}
                }
            }
            setLoading(false);
        } catch (err) {
            console.error("Lỗi API thống kê:", err);
            setLoading(false);
        }
    };

    useEffect(() => {
        if ((startDate && endDate) || (!startDate && !endDate)) {
            fetchStatistic(startDate, endDate);
        }
    }, [startDate, endDate]);

    const handleSetAllTime = () => {
        setDateRange([null, null]);
    };

    const handleSetToday = () => {
        const today = new Date();
        setDateRange([today, today]);
    };

    const handleSetYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        setDateRange([yesterday, yesterday]);
    };

    const handleSetThisWeek = () => {
        const now = new Date();
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(now.setDate(diff));
        setDateRange([monday, new Date()]);
    };

    const handleSetThisMonth = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        setDateRange([firstDay, new Date()]);
    };

    const handleSetLastMonth = () => {
        const now = new Date();
        const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        setDateRange([firstDayLastMonth, lastDayLastMonth]);
    };

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box component="main" className="happy-dashboard-page">
                    {isLoading && (
                        <div className="loading" style={{ maxWidth: "100%", zIndex: 9999 }}>
                            <div className="loader"></div>
                        </div>
                    )}

                    <Container maxWidth={false} className="happy-dashboard-container">
                        {/* Top Filter Block with Single Row Horizontal Alignment */}
                        <div className="ax-filter-block happy-dashboard-filter">
                            <div className="happy-dashboard-filter-row">
                                <span className="happy-dashboard-filter-label"><i />Khoảng thời gian</span>
                                <div className="happy-dashboard-date-box">
                                    <DatePicker
                                        selectsRange={true}
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(update) => setDateRange(update)}
                                        maxDate={new Date()}
                                        dateFormat="yyyy-MM-dd"
                                        customInput={<HappyRangeDateInput startDate={startDate} endDate={endDate} />}
                                    />
                                </div>
                                <div className="happy-dashboard-quick-presets">
                                    <button type="button" className={`happy-quick-btn btn-all ${!startDate && !endDate ? "active" : ""}`} onClick={handleSetAllTime}>Tất cả ngày</button>
                                    <button type="button" className="happy-quick-btn btn-today" onClick={handleSetToday}>Hôm nay</button>
                                    <button type="button" className="happy-quick-btn btn-yesterday" onClick={handleSetYesterday}>Hôm qua</button>
                                    <button type="button" className="happy-quick-btn btn-week" onClick={handleSetThisWeek}>Tuần này</button>
                                    <button type="button" className="happy-quick-btn btn-month" onClick={handleSetThisMonth}>Tháng này</button>
                                    <button type="button" className="happy-quick-btn btn-lastmonth" onClick={handleSetLastMonth}>Tháng trước</button>
                                </div>

                                <button
                                    className="ax-btn-search-red"
                                    onClick={() => fetchStatistic(startDate, endDate)}
                                >
                                    Tìm kiếm
                                </button>
                            </div>
                        </div>

                        {/* 18 Gradient Cards Grid arranged 5 items per row */}
                        <div className="ax-dash-grid">
                            {/* Card 1 */}
                            <div className="ax-grad-card g1">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><AccountBalanceWalletRoundedIcon /></div>
                                    <div className="ax-card-badge"><TrendingUpRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">SỐ ĐIỂM HỆ THỐNG</div>
                                    <div className="ax-card-val">{data?.sodiemhethong ? (Number(data.sodiemhethong) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill"></div></div>
                            </div>

                            {/* Card 2 */}
                            <div className="ax-grad-card g2">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><GroupsRoundedIcon /></div>
                                    <div className="ax-card-badge"><PersonRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THÀNH VIÊN</div>
                                    <div className="ax-card-val">{data?.thanhvien ? Number(data.thanhvien).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "75%" }}></div></div>
                            </div>

                            {/* Card 3 */}
                            <div className="ax-grad-card g3">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><PersonAddAlt1RoundedIcon /></div>
                                    <div className="ax-card-badge"><AutoAwesomeRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TV ĐĂNG KÝ HÔM NAY</div>
                                    <div className="ax-card-val">{data?.userToday ? Number(data.userToday).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "30%" }}></div></div>
                            </div>

                            {/* Card 4 */}
                            <div className="ax-grad-card g4">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><CreditCardRoundedIcon /></div>
                                    <div className="ax-card-badge"><ArrowUpwardRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG NẠP</div>
                                    <div className="ax-card-val">{data?.tongnaps ? (Number(data.tongnaps) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "65%" }}></div></div>
                            </div>

                            {/* Card 5 */}
                            <div className="ax-grad-card g5">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><PaymentsRoundedIcon /></div>
                                    <div className="ax-card-badge"><ArrowDownwardRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG RÚT</div>
                                    <div className="ax-card-val">{data?.tongruts ? (Number(data.tongruts) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "40%" }}></div></div>
                            </div>

                            {/* Card 6 */}
                            <div className="ax-grad-card g6">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><AdminPanelSettingsRoundedIcon /></div>
                                    <div className="ax-card-badge"><ArrowUpwardRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG NẠP BỞI ADMIN</div>
                                    <div className="ax-card-val">{data?.tongnapadmin ? (Number(data.tongnapadmin) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "50%" }}></div></div>
                            </div>

                            {/* Card 7 */}
                            <div className="ax-grad-card g7">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><PriceCheckRoundedIcon /></div>
                                    <div className="ax-card-badge"><ArrowDownwardRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG RÚT BỞI ADMIN</div>
                                    <div className="ax-card-val">{data?.tongrutadmin ? (Number(data.tongrutadmin) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "10%" }}></div></div>
                            </div>

                            {/* Card 8 */}
                            <div className="ax-grad-card g8">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><ReplayRoundedIcon /></div>
                                    <div className="ax-card-badge"><AutoAwesomeRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">HOÀN TRẢ</div>
                                    <div className="ax-card-val">{data?.hoantra ? (Number(data.hoantra) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "55%" }}></div></div>
                            </div>

                            {/* Card 9 */}
                            <div className="ax-grad-card g9">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><CardGiftcardRoundedIcon /></div>
                                    <div className="ax-card-badge"><AutoAwesomeRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">KHUYẾN MÃI</div>
                                    <div className="ax-card-val">{data?.khuyenmai ? (Number(data.khuyenmai) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "15%" }}></div></div>
                            </div>

                            {/* Card 10 */}
                            <div className="ax-grad-card g10">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><ExtensionRoundedIcon /></div>
                                    <div className="ax-card-badge"><SportsEsportsRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG CƯỢC GAME API</div>
                                    <div className="ax-card-val">{data?.tongcuocapi ? (Number(data.tongcuocapi) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "60%" }}></div></div>
                            </div>

                            {/* Card 11 */}
                            <div className="ax-grad-card g11">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><EmojiEventsRoundedIcon /></div>
                                    <div className="ax-card-badge"><AutoAwesomeRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THẮNG GAME API</div>
                                    <div className="ax-card-val">{data?.tongthangapi ? (Number(data.tongthangapi) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "50%" }}></div></div>
                            </div>

                            {/* Card 12 */}
                            <div className="ax-grad-card g12">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><TrendingDownRoundedIcon /></div>
                                    <div className="ax-card-badge"><ArrowDownwardRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THUA GAME API</div>
                                    <div className="ax-card-val">{data?.tongthuaapi ? (Number(data.tongthuaapi) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "45%" }}></div></div>
                            </div>

                            {/* Card 13 */}
                            <div className="ax-grad-card g13">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><SportsEsportsRoundedIcon /></div>
                                    <div className="ax-card-badge"><CasinoRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG CƯỢC MINI GAME</div>
                                    <div className="ax-card-val">{data?.tongcuocminigame ? (Number(data.tongcuocminigame) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "80%" }}></div></div>
                            </div>

                            {/* Card 14 */}
                            <div className="ax-grad-card g14">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><MilitaryTechRoundedIcon /></div>
                                    <div className="ax-card-badge"><AutoAwesomeRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THẮNG MINI GAME</div>
                                    <div className="ax-card-val">{data?.tongthangminigame ? (Number(data.tongthangminigame) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "55%" }}></div></div>
                            </div>

                            {/* Card 15 */}
                            <div className="ax-grad-card g15">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><BarChartRoundedIcon /></div>
                                    <div className="ax-card-badge"><ArrowDownwardRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THUA MINI GAME</div>
                                    <div className="ax-card-val">{data?.tongthuaminigame ? (Number(data.tongthuaminigame) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "50%" }}></div></div>
                            </div>

                            {/* Card 16 */}
                            <div className="ax-grad-card g16">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><GridViewRoundedIcon /></div>
                                    <div className="ax-card-badge"><AutoAwesomeRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG CƯỢC BLOCKCHAIN</div>
                                    <div className="ax-card-val">{data?.tongcuocblockchain ? (Number(data.tongcuocblockchain) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "65%" }}></div></div>
                            </div>

                            {/* Card 17 */}
                            <div className="ax-grad-card g17">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><WorkspacePremiumRoundedIcon /></div>
                                    <div className="ax-card-badge"><AutoAwesomeRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THẮNG BLOCKCHAIN</div>
                                    <div className="ax-card-val">{data?.tongthangblockchain ? (Number(data.tongthangblockchain) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "50%" }}></div></div>
                            </div>

                            {/* Card 18 */}
                            <div className="ax-grad-card g18">
                                <div className="ax-card-top">
                                    <div className="ax-card-icon-box"><ShowChartRoundedIcon /></div>
                                    <div className="ax-card-badge"><ArrowDownwardRoundedIcon /></div>
                                </div>
                                <div>
                                    <div className="ax-card-label">TỔNG THUA BLOCKCHAIN</div>
                                    <div className="ax-card-val">{data?.tongthuablockchain ? (Number(data.tongthuablockchain) / 1000).toLocaleString("vi-VN") : "0"}</div>
                                </div>
                                <div className="ax-card-bar"><div className="ax-card-bar-fill" style={{ width: "45%" }}></div></div>
                            </div>

                        </div>
                    </Container>
                </Box>
                {isGuideOpen && (
                    <div className="ax-modal-overlay" onClick={() => setIsGuideOpen(false)}>
                        <div className="ax-modal-card" onClick={(e) => e.stopPropagation()}>
                            <div className="ax-modal-header">
                                <h3>Hướng dẫn chi tiết Dashboard</h3>
                                <span className="close-icon" onClick={() => setIsGuideOpen(false)}>✕</span>
                            </div>
                            <div className="ax-modal-body">
                                <h4>Khoảng thời gian</h4>
                                <ul>
                                    <li>Được sử dụng để tra cứu thống kê số liệu tổng hợp hệ thống theo mốc thời gian tùy chọn.</li>
                                </ul>

                                <h4>Các chỉ số hệ thống</h4>
                                <ul>
                                    <li>Hiển thị số liệu hệ thống, nạp/rút và thống kê cược/thắng/thua của Game API, Mini Game và Blockchain.</li>
                                </ul>
                            </div>
                            <div className="ax-modal-footer">
                                <button className="ax-btn-modal-close" onClick={() => setIsGuideOpen(false)}>
                                    Đóng
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default Dashboard;
